import * as Astronomy from 'astronomy-engine';

/**
 * Synodic month, and the moon age at full moon.
 *
 * These must stay identical to LUNAR_CYCLE_DAYS / FULL_MOON_DAYS in
 * packages/server/src/services/exoplanet/astronomyHelpers.js and in
 * packages/python-scheduler/moon_avoidance.py. The shared golden vectors in
 * packages/python-scheduler/test-fixtures/moon_avoidance_vectors.json assert all three against the
 * same numbers -- three implementations of one formula in two languages is how this drifted in the
 * first place (#1157).
 */
export const LUNAR_CYCLE_DAYS = 29.530588;
export const FULL_MOON_DAYS = LUNAR_CYCLE_DAYS / 2;

/**
 * Client-side astronomy calculations service
 * Eliminates need for server-side astronomical data in telemetry
 */

export class AstronomyService {
  constructor() {
    this.updateInterval = null;
    this.callbacks = new Map(); // Map of callbacks to their observatory configs
  }

  /**
   * Get the sun altitude threshold for a given twilight type
   * @param {string} twilightType - Type of twilight ('civil', 'nautical', 'astronomical', 'custom')
   * @param {number} customAltitude - Custom altitude for 'custom' type
   * @returns {number} Sun altitude threshold in degrees
   */
  getTwilightAltitude(twilightType = 'astronomical', customAltitude = -18) {
    switch (twilightType) {
      case 'civil':
        return -6;
      case 'nautical':
        return -12;
      case 'astronomical':
        return -18;
      case 'custom':
        return customAltitude;
      default:
        return -18; // Default to astronomical
    }
  }

  /**
   * Calculate all astronomical data for an observatory
   * @param {number} latitude - Observatory latitude in degrees
   * @param {number} longitude - Observatory longitude in degrees  
   * @param {number} altitude - Observatory altitude in meters (optional)
   * @param {Date} date - Date for calculations (defaults to now)
   * @param {Object} config - Configuration including twilight settings
   * @returns {Object} Astronomical data
   */
  calculateAstronomicalData(latitude, longitude, altitude = 0, date = new Date(), config = {}) {
    try {
      // Create observer location
      const observer = new Astronomy.Observer(latitude, longitude, altitude);
      
      // Get sun position
      const sunEquator = Astronomy.Equator(Astronomy.Body.Sun, date, observer, true, true);
      // Use JPL Horizons refraction model for more accurate results at professional observatories
      const sunHorizon = Astronomy.Horizon(date, observer, sunEquator.ra, sunEquator.dec, 'jplhor');
      
      // The altitude returned is for the CENTER of the sun's disk
      // For upper limb (common in astronomy), add the sun's semi-diameter (~0.267°)
      // sunHorizon.altitude contains the center disk altitude

      // Get moon position
      const moonEquator = Astronomy.Equator(Astronomy.Body.Moon, date, observer, true, true);
      // Use JPL Horizons refraction model for consistency
      const moonHorizon = Astronomy.Horizon(date, observer, moonEquator.ra, moonEquator.dec, 'jplhor');
      
      // Get moon phase
      const moonPhaseAngle = Astronomy.MoonPhase(date);
      
      // Determine moon phase name
      let moonPhaseName;
      if (moonPhaseAngle < 11.25 || moonPhaseAngle >= 348.75) {
        moonPhaseName = 'New Moon';
      } else if (moonPhaseAngle < 78.75) {
        moonPhaseName = 'Waxing Crescent';
      } else if (moonPhaseAngle < 101.25) {
        moonPhaseName = 'First Quarter';
      } else if (moonPhaseAngle < 168.75) {
        moonPhaseName = 'Waxing Gibbous';
      } else if (moonPhaseAngle < 191.25) {
        moonPhaseName = 'Full Moon';
      } else if (moonPhaseAngle < 258.75) {
        moonPhaseName = 'Waning Gibbous';
      } else if (moonPhaseAngle < 281.25) {
        moonPhaseName = 'Last Quarter';
      } else {
        moonPhaseName = 'Waning Crescent';
      }
      
      // Calculate moon illumination (0-1)
      const moonIllumination = (1 - Math.cos(moonPhaseAngle * Math.PI / 180)) / 2;
      
      // Calculate Local Sidereal Time
      const lst = Astronomy.SiderealTime(date) + (longitude / 15);
      
      // Get twilight altitude from configuration
      const twilightType = config.twilight_type || 'astronomical';
      const customAltitude = config.custom_sun_altitude || -18;
      const twilightAltitude = this.getTwilightAltitude(twilightType, customAltitude);
      
      // Calculate twilight times based on configured altitude
      const isDark = sunHorizon.altitude < twilightAltitude;
      let nextEvent, nextEventType;
      
      if (isDark) {
        // It's dark (past twilight), find next dawn
        const nextDawn = Astronomy.SearchAltitude(Astronomy.Body.Sun, observer, +1, date, 1, twilightAltitude);
        if (nextDawn) {
          nextEvent = nextDawn.date;
          nextEventType = 'dawn';
        }
      } else {
        // It's light, find next dusk
        const nextDusk = Astronomy.SearchAltitude(Astronomy.Body.Sun, observer, -1, date, 1, twilightAltitude);
        if (nextDusk) {
          nextEvent = nextDusk.date;
          nextEventType = 'dusk';
        }
      }
      
      // Also calculate standard sunrise/sunset for reference
      const standardSunrise = Astronomy.SearchRiseSet(Astronomy.Body.Sun, observer, +1, date, 1);
      const standardSunset = Astronomy.SearchRiseSet(Astronomy.Body.Sun, observer, -1, date, 1);
      
      return {
        sun_altitude: sunHorizon.altitude,
        moon_altitude: moonHorizon.altitude,
        moon_illumination: moonIllumination,
        moon_phase: moonPhaseName,
        local_sidereal_time: lst,
        next_sun_event: nextEvent ? nextEvent.toISOString() : null,
        next_sun_event_type: nextEventType || null,
        twilight_type: twilightType,
        twilight_altitude: twilightAltitude,
        is_dark: isDark,
        standard_sunrise: standardSunrise ? standardSunrise.date.toISOString() : null,
        standard_sunset: standardSunset ? standardSunset.date.toISOString() : null,
        calculated_at: date.toISOString()
      };
    } catch (error) {
      console.error('Error calculating astronomical data:', error);
      return {
        sun_altitude: null,
        moon_altitude: null,
        moon_illumination: null,
        moon_phase: null,
        local_sidereal_time: null,
        next_sun_event: null,
        next_sun_event_type: null,
        calculated_at: date.toISOString(),
        error: error.message
      };
    }
  }

  /**
   * Calculate rise and set times for a celestial object
   * @param {number} ra - Right ascension in degrees
   * @param {number} dec - Declination in degrees
   * @param {number} latitude - Observatory latitude in degrees
   * @param {number} longitude - Observatory longitude in degrees
   * @param {number} altitude - Observatory altitude in meters (optional)
   * @param {Date} date - Date for calculations (defaults to now)
   * @param {number} minAltitude - Minimum altitude for observing in degrees (default 30°)
   * @returns {Object} Rise and set times
   */
  /**
   * Calculate rise/set times for objects with orbital elements (moving objects)
   * @param {Object} orbitalElements - Orbital elements from JPL Horizons
   * @param {number} latitude - Observer latitude in degrees
   * @param {number} longitude - Observer longitude in degrees
   * @param {number} altitude - Observer altitude in meters
   * @param {Date} date - Date for calculations
   * @param {number} minAltitude - Minimum altitude threshold in degrees
   * @param {function} computePositionFn - Function to compute RA/Dec from orbital elements
   * @returns {Object} Rise/set times and max altitude
   */
  calculateRiseSetTimesMoving(orbitalElements, latitude, longitude, altitude = 0, date = new Date(), minAltitude = 30, computePositionFn) {
    try {
      const observer = new Astronomy.Observer(latitude, longitude, altitude);

      // Find when the object reaches minimum observable altitude (default 30°)
      // We'll search within a 24-hour window starting at noon UTC
      const searchStart = new Date(date);
      searchStart.setUTCHours(12, 0, 0, 0); // Start at noon UTC

      let riseTime = null;
      let setTime = null;
      let transitTime = null;
      let maxAltitude = null;

      // Calculate positions throughout the day to find rise/set/transit
      // For moving objects, recompute RA/Dec at each time point
      const positions = [];
      for (let hour = 0; hour < 24; hour++) {
        const checkTime = new Date(searchStart);
        checkTime.setHours(checkTime.getHours() + hour);

        // Compute position at this specific time using orbital elements
        const position = computePositionFn(orbitalElements, checkTime);
        const raHours = position.ra / 15; // Convert degrees to hours

        const horizon = Astronomy.Horizon(checkTime, observer, raHours, position.dec, 'normal');
        positions.push({ time: checkTime, altitude: horizon.altitude, azimuth: horizon.azimuth });
      }

      // Find rise time (altitude crosses minAltitude from below)
      for (let i = 1; i < positions.length; i++) {
        if (positions[i-1].altitude < minAltitude && positions[i].altitude >= minAltitude) {
          // Interpolate to find more precise rise time
          const fraction = Math.abs(positions[i-1].altitude) /
                          (Math.abs(positions[i-1].altitude) + positions[i].altitude);
          riseTime = new Date(positions[i-1].time.getTime() +
                             fraction * (positions[i].time.getTime() - positions[i-1].time.getTime()));
          break;
        }
      }

      // Find set time (altitude crosses minAltitude from above)
      for (let i = 1; i < positions.length; i++) {
        if (positions[i-1].altitude >= minAltitude && positions[i].altitude < minAltitude) {
          // Interpolate to find more precise set time
          const fraction = (positions[i-1].altitude - minAltitude) /
                          (positions[i-1].altitude - positions[i].altitude);
          setTime = new Date(positions[i-1].time.getTime() +
                            fraction * (positions[i].time.getTime() - positions[i-1].time.getTime()));
          break;
        }
      }

      // Find transit (maximum altitude)
      maxAltitude = Math.max(...positions.map(p => p.altitude));
      const maxAltIndex = positions.findIndex(p => p.altitude === maxAltitude);
      if (maxAltIndex >= 0) {
        transitTime = positions[maxAltIndex].time;
      }

      // Check for circumpolar or never-rising cases
      const minAlt = Math.min(...positions.map(p => p.altitude));
      const isCircumpolar = minAlt >= minAltitude;
      const neverRises = maxAltitude < minAltitude;

      return {
        riseTime,
        setTime,
        transitTime,
        maxAltitude,
        isCircumpolar,
        neverRises
      };
    } catch (error) {
      console.error('Error calculating rise/set times for moving object:', error);
      return null;
    }
  }

  calculateRiseSetTimes(ra, dec, latitude, longitude, altitude = 0, date = new Date(), minAltitude = 30) {
    try {
      const observer = new Astronomy.Observer(latitude, longitude, altitude);

      // Convert RA from degrees to hours for astronomy-engine
      const raHours = ra / 15;

      // Find when the object reaches minimum observable altitude (default 30°)
      // We'll search within a 24-hour window starting at noon UTC
      const searchStart = new Date(date);
      searchStart.setUTCHours(12, 0, 0, 0); // Start at noon UTC

      let riseTime = null;
      let setTime = null;
      let transitTime = null;
      let maxAltitude = null;

      // Calculate positions throughout the day to find rise/set/transit
      const positions = [];
      for (let hour = 0; hour < 24; hour++) {
        const checkTime = new Date(searchStart);
        checkTime.setHours(checkTime.getHours() + hour);

        const horizon = Astronomy.Horizon(checkTime, observer, raHours, dec, 'normal');
        positions.push({ time: checkTime, altitude: horizon.altitude, azimuth: horizon.azimuth });
      }

      // Find rise time (altitude crosses minAltitude from below)
      for (let i = 1; i < positions.length; i++) {
        if (positions[i-1].altitude < minAltitude && positions[i].altitude >= minAltitude) {
          // Interpolate to find more precise rise time
          const fraction = Math.abs(positions[i-1].altitude) / 
                          (Math.abs(positions[i-1].altitude) + positions[i].altitude);
          riseTime = new Date(positions[i-1].time.getTime() + 
                             fraction * (positions[i].time.getTime() - positions[i-1].time.getTime()));
          break;
        }
      }
      
      // Find set time (altitude crosses minAltitude from above)
      for (let i = 1; i < positions.length; i++) {
        if (positions[i-1].altitude >= minAltitude && positions[i].altitude < minAltitude) {
          // Interpolate to find more precise set time
          const fraction = (positions[i-1].altitude - minAltitude) /
                          (positions[i-1].altitude - positions[i].altitude);
          setTime = new Date(positions[i-1].time.getTime() +
                            fraction * (positions[i].time.getTime() - positions[i-1].time.getTime()));
          break;
        }
      }
      
      // Find transit (maximum altitude)
      let maxAlt = -90;
      let transitIndex = 0;
      for (let i = 0; i < positions.length; i++) {
        if (positions[i].altitude > maxAlt) {
          maxAlt = positions[i].altitude;
          transitIndex = i;
        }
      }

      // Refine transit time using quadratic interpolation
      if (transitIndex > 0 && transitIndex < positions.length - 1) {
        // For equally-spaced points (1 hour apart), use simplified parabolic interpolation
        const a0 = positions[transitIndex - 1].altitude;
        const a1 = positions[transitIndex].altitude;
        const a2 = positions[transitIndex + 1].altitude;

        // Calculate the curvature of the parabola
        const curvature = a0 - 2*a1 + a2;

        // Only interpolate if there's actually a peak (curvature < 0)
        if (curvature < -0.0001) {
          // Calculate the fractional offset from the middle point to the vertex
          // For a parabola through three equally-spaced points, the vertex offset is:
          // offset = (a0 - a2) / (2 * (a0 - 2*a1 + a2))
          const offset = (a0 - a2) / (2 * curvature);

          // Offset should be between -0.5 and 0.5 (within the interval)
          if (offset >= -0.5 && offset <= 0.5) {
            // Calculate interpolated transit time
            const hourInMs = 3600000; // 1 hour in milliseconds
            const baseTime = positions[transitIndex].time.getTime();
            const interpolatedTime = baseTime + (offset * hourInMs);
            transitTime = new Date(interpolatedTime);

            // Calculate interpolated maximum altitude
            // For a parabola: max_altitude = a1 - (a0 - a2)² / (8 * curvature)
            maxAltitude = a1 - ((a0 - a2) * (a0 - a2)) / (8 * curvature);
          } else {
            // Offset out of range, use the sample
            transitTime = positions[transitIndex].time;
            maxAltitude = maxAlt;
          }
        } else {
          // Not a proper peak (flat or inverted), use the sample
          transitTime = positions[transitIndex].time;
          maxAltitude = maxAlt;
        }
      } else {
        // Edge case: maximum at first or last position
        transitTime = positions[transitIndex].time;
        maxAltitude = maxAlt;
      }
      
      // Determine if the object is circumpolar or never visible
      const isCircumpolar = dec > (90 - Math.abs(latitude));
      const isNeverVisible = dec < -(90 - Math.abs(latitude));
      
      return {
        riseTime: riseTime,
        setTime: setTime,
        transitTime: transitTime,
        maxAltitude: maxAltitude,
        isCircumpolar: isCircumpolar,
        neverRises: isNeverVisible,
        calculatedFor: date
      };
    } catch (error) {
      console.error('Error calculating rise/set times:', error);
      return {
        riseTime: null,
        setTime: null,
        transitTime: null,
        maxAltitude: null,
        error: error.message
      };
    }
  }

  /**
   * Start real-time updates for astronomical data
   * @param {Object} observatory - Observatory with location data
   * @param {Function} callback - Called with updated data every minute
   * @returns {Function} Cleanup function to stop updates
   */
  startRealTimeUpdates(observatory, callback) {
    if (!observatory?.location?.latitude || !observatory?.location?.longitude) {
      console.warn('Observatory location not available for astronomy calculations');
      return () => {};
    }

    // Store callback with its observatory config
    this.callbacks.set(callback, observatory);

    // Extract twilight configuration from observatory
    const config = {
      twilight_type: observatory.configuration?.twilight_type || 'astronomical',
      custom_sun_altitude: observatory.configuration?.custom_sun_altitude || -18
    };

    // Send initial calculation
    const initialData = this.calculateAstronomicalData(
      observatory.location.latitude,
      observatory.location.longitude,
      observatory.location.altitude_meters || 0,
      new Date(),
      config
    );
    callback(initialData);

    // Start updates if not already running
    if (!this.updateInterval) {
      this.updateInterval = setInterval(() => {
        // Call all registered callbacks with their specific observatory configs
        this.callbacks.forEach((obs, cb) => {
          try {
            const obsConfig = {
              twilight_type: obs.configuration?.twilight_type || 'astronomical',
              custom_sun_altitude: obs.configuration?.custom_sun_altitude || -18
            };
            
            const data = this.calculateAstronomicalData(
              obs.location.latitude,
              obs.location.longitude,
              obs.location.altitude_meters || 0,
              new Date(),
              obsConfig
            );
            
            cb(data);
          } catch (error) {
            console.error('Error in astronomy callback:', error);
          }
        });
      }, 1000); // Update every second for real-time LST and sun/moon positions
    }

    // Return cleanup function
    return () => {
      this.callbacks.delete(callback);
      if (this.callbacks.size === 0 && this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
    };
  }

  /**
   * Calculate time remaining until next sun event
   * @param {string} nextSunEvent - ISO string of next event time
   * @returns {string} Human-readable time remaining
   */
  calculateTimeRemaining(nextSunEvent) {
    if (!nextSunEvent) return null;
    
    const now = new Date();
    const eventTime = new Date(nextSunEvent);
    const diffMs = eventTime - now;
    
    if (diffMs <= 0) return 'Now';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }

  /**
   * Calculate the Lorentzian moon separation requirement based on days from new moon
   * @param {number} daysFromNew - Days since new moon (0-29.53)
   * @param {number} peakSeparation - Maximum separation required at full moon (degrees)
   * @param {number} width - Lorentzian HWHM width in days
   * @returns {number} Required minimum moon separation in degrees
   */
  calculateLorentzianMoonSeparation(daysFromNew, peakSeparation = 60, width = 5) {
    // Moon Avoidance Lorentzian: L(x) = A * γ² / (γ² + (x - x₀)²)
    //   A  = peak amplitude, the separation required AT FULL MOON
    //   γ  = half-width at half-maximum, in days
    //   x  = days from new moon
    //   x₀ = full moon, at HALF a lunar cycle
    //
    // #1157: x₀ is 14.765, not the 14 this used to hardcode (#105 recorded that as a deliberate
    // simplification). It costs up to 4.5° and is asymmetric about the true centre, so it
    // over-blocks the waxing half of the month and under-blocks the waning half.
    //
    // The result is NOT rounded: it is checked against the Python scheduler and the server copy
    // through shared golden vectors at 1e-9, and rounding here would have hidden a divergence of
    // up to half a degree. Round at the point of display instead.
    if (!(width > 0)) return 0;

    const age = ((daysFromNew % LUNAR_CYCLE_DAYS) + LUNAR_CYCLE_DAYS) % LUNAR_CYCLE_DAYS;
    const widthSquared = width * width;
    const distanceFromFull = age - FULL_MOON_DAYS;
    const distanceSquared = distanceFromFull * distanceFromFull;

    return peakSeparation * widthSquared / (widthSquared + distanceSquared);
  }
  
  /**
   * Calculate days from new moon for a given date
   * @param {Date} date - Date to calculate for
   * @returns {number} Days since new moon (0-29.53)
   */
  getDaysFromNewMoon(date = new Date()) {
    // Get moon phase angle (0-360 degrees)
    const moonPhaseAngle = Astronomy.MoonPhase(date);
    
    // Convert to days (0 = new moon, LUNAR_CYCLE_DAYS / 2 = full moon)
    return (moonPhaseAngle / 360) * LUNAR_CYCLE_DAYS;
  }
  
  /**
   * Format Local Sidereal Time for display
   * @param {number} lst - LST in decimal hours
   * @returns {string} Formatted LST (HH:MM:SS)
   */
  formatLST(lst) {
    if (lst === null || lst === undefined) return 'N/A';
    
    // Normalize to 0-24 hours
    const normalizedLST = ((lst % 24) + 24) % 24;
    
    const hours = Math.floor(normalizedLST);
    const minutes = Math.floor((normalizedLST - hours) * 60);
    const seconds = Math.floor(((normalizedLST - hours) * 60 - minutes) * 60);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Format altitude for display
   * @param {number} altitude - Altitude in degrees
   * @returns {string} Formatted altitude with degree symbol
   */
  formatAltitude(altitude) {
    if (altitude === null || altitude === undefined) return 'N/A';
    return `${altitude.toFixed(1)}°`;
  }

  /**
   * Format illumination percentage
   * @param {number} illumination - Illumination fraction (0-1)
   * @returns {string} Formatted percentage
   */
  formatIllumination(illumination) {
    if (illumination === null || illumination === undefined) return 'N/A';
    return `${(illumination * 100).toFixed(0)}%`;
  }
}

// Export singleton instance
export const astronomyService = new AstronomyService();
export default astronomyService;